import React, { Component, useMemo, useState } from 'react';
import propTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';
import { useSelector } from 'react-redux';
import photoPlaceholder from 'assets/images/user-photo-placeholder.svg';
import IOSSwitch from '../../../../../IOSSwitch';
import HoursBlock from '../../../HoursBlock';
import styles from './styles';
import { isValidTimePeriods, isHoursOverlapseTimeOffs } from '../../../../helpers';
import StartTimeBlock from '../StartTimeBlock';
import Divider from '@material-ui/core/Divider';
import { add, differenceInDays, set, setMinutes } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

const useProjectAlternates = (sheet) => {
  const allProjects = useSelector((store) => store.projectTypes.projects.data);
  const liveProjectId = sheet?.project?.oldProjectId || sheet?.projectId;
  return useMemo(() => {
    let project = null;
    if (sheet?.project) project = sheet.project;
    else if (liveProjectId) project = allProjects.find((p) => p._id === liveProjectId) || null;
    return project?.alternateLaborClassifications || [];
  }, [sheet?.project, liveProjectId, allProjects]);
};

const LcPickerRow = ({ classes, value, options, nameById, onChange, onRemove }) => (
  <div className={classes.lcBlock}>
    <div className={classes.lcBlockContent}>
      <Typography variant="body1" color="textSecondary">
        Labor Classification:
      </Typography>
      <TextField
        select
        value={value || ''}
        onChange={(e) => onChange(e.target.value || null)}
        variant="outlined"
        size="small"
        fullWidth
        className={classes.lcSelect}
      >
        {options.map((id) => (
          <MenuItem key={id} value={id}>
            {nameById?.[id]?.name || id}
          </MenuItem>
        ))}
      </TextField>
    </div>
    <IconButton
      className={classes.lcDeleteButton}
      onClick={onRemove}
      aria-label="Remove labor classification"
      disableTouchRipple
    >
      <CloseIcon />
    </IconButton>
  </div>
);

// Mutex invariant: worker-level LC may persist only while worker.hours is empty.
// On empty -> non-empty transition, inherit worker LC into the first hours entry
// (BE strips the worker-level value anyway when hours are non-empty).
const applyWorkerLcMigration = (workerData, updatedHours) => {
  const wasEmpty = !workerData?.hours?.length;
  const willHave = !!updatedHours?.length;
  const workerLc = workerData?.laborClassificationId || null;
  if (!wasEmpty || !willHave || !workerLc) {
    return { hours: updatedHours, laborClassificationId: workerLc };
  }
  const [first, ...rest] = updatedHours;
  return {
    hours: [
      { ...first, laborClassificationId: first.laborClassificationId || workerLc },
      ...rest,
    ],
    laborClassificationId: null,
  };
};

const WorkerLcBlock = ({ classes, sheet, workerData, isPicking, setIsPicking, setWorkerLc }) => {
  const alternates = useProjectAlternates(sheet);
  const nameById = useSelector((store) => store.laborClassifications.byId);

  if (workerData?.hours?.length) return null;

  const rawLcId = typeof workerData?.laborClassificationId === 'string' && workerData.laborClassificationId
    ? workerData.laborClassificationId
    : null;
  const lcValue = rawLcId && alternates.includes(rawLcId) ? rawLcId : null;

  const showLcBlock = !!lcValue || isPicking;
  if (!showLcBlock) return null;

  return (
    <Grid item xs={12} className={classes.paddingBottom}>
      <LcPickerRow
        classes={classes}
        value={lcValue}
        options={alternates}
        nameById={nameById}
        onChange={(lcId) => setWorkerLc(lcId)}
        onRemove={() => {
          if (lcValue) setWorkerLc(null);
          setIsPicking(false);
        }}
      />
    </Grid>
  );
};

const WorkerLcAddButton = ({ classes, sheet, workerData, isPicking, setIsPicking }) => {
  const alternates = useProjectAlternates(sheet);
  if (workerData?.hours?.length) return null;
  if (workerData?.laborClassificationId) return null;
  if (isPicking) return null;
  if (!alternates.length) return null;
  return (
    // <Grid item xs={6} md={4} className={classes.textCenter}>
      <Button
        variant="text"
        onClick={() => setIsPicking(true)}
        className={classnames(classes.addButton, classes.textCenter)}
        disableRipple
        disableTouchRipple
      >
        + Labor Classification
      </Button>
    // </Grid>
  );
};

const Pair = ({
  classes,
  sheet,
  workerData,
  h,
  i,
  isAnyCropped,
  addResourceHours,
  addTravelTime,
  removeResourceHours,
  removeResourceTravelTime,
  updateResourceHours,
}) => {
  const alternates = useProjectAlternates(sheet);

  const rawLcId = typeof h.laborClassificationId === 'string' && h.laborClassificationId
    ? h.laborClassificationId
    : null;
  // A stored id only counts as a per-pair override when it's still one of the
  // project's current alternates. If alternates change after the pair was
  // saved (or BE returns a stale/auto-populated id), fall back to the
  // "+ Add Labor Classification" button rather than showing a broken select.
  const lcValue = rawLcId && alternates.includes(rawLcId) ? rawLcId : null;
  const nameById = useSelector((store) => store.laborClassifications.byId);

  const [isPicking, setIsPicking] = useState(false);

  const setLc = (lcId) => updateResourceHours(i)({ ...h, laborClassificationId: lcId });

  const showLcBlock = !!lcValue || isPicking;
  const showLcButton = !lcValue && !isPicking && alternates.length > 0;

  return (
    <>
      <Grid item xs={12} className={classes.hoursBlock}>
        <HoursBlock
          sheet={sheet}
          itemData={workerData}
          itemHours={h}
          hoursIndex={i}
          deleteTime={removeResourceHours(i)}
          deleteTravelTime={removeResourceTravelTime(i)}
          updateResourceHours={updateResourceHours(i)}
          availableUserHours={workerData.availableHours}
          isWorkersType
          isAnyCropped={isAnyCropped}
        />
      </Grid>

      {showLcBlock && (
        <Grid item xs={12} className={classes.paddingBottom}>
          <LcPickerRow
            classes={classes}
            value={lcValue}
            options={alternates}
            nameById={nameById}
            onChange={(lcId) => setLc(lcId)}
            onRemove={() => {
              if (lcValue) setLc(null);
              setIsPicking(false);
            }}
          />
        </Grid>
      )}

      <Grid item xs={12} className={classes.paddingBottom}>
        <div className={classes.pairActionsRow}>
          {h.isWlHours && !sheet.travelTimeOnly && (
            <Button
              variant="text"
              onClick={() => addResourceHours(i)}
              className={classes.addButton}
              disableRipple
              disableTouchRipple
            >
              + Add Hours
            </Button>
          )}
          {h.isWlTravelTime && !sheet.travelTimeOnly && (
            <Button
              variant="text"
              onClick={() => addTravelTime(i)}
              className={classnames(classes.addButton, classes.textCenter)}
              disableRipple
              disableTouchRipple
            >
              + Individual Travel Time
            </Button>
          )}
          {showLcButton && (
            <Button
              variant="text"
              onClick={() => setIsPicking(true)}
              className={classes.addButton}
              disableRipple
              disableTouchRipple
            >
              + Add Labor Classification
            </Button>
          )}
        </div>
      </Grid>
    </>
  );
};

class SelectedUser extends Component {
  state = {
    isPickingWorkerLc: false,
  };

  setIsPickingWorkerLc = (value) => this.setState({ isPickingWorkerLc: value });

  setWorkerLc = (lcId) => {
    const { updateSelectedResource, workerData } = this.props;
    const updatedData = {
      ...workerData,
      laborClassificationId: lcId || null,
    };
    if (!lcId) this.setState({ isPickingWorkerLc: false });
    updateSelectedResource(workerData._id, updatedData);
  };

  setForeman = () => {
    const { updateSelectedResource, workerData } = this.props;
    const updatedData = {
      ...workerData,
      foreman: !workerData.foreman,
    };
    updateSelectedResource(workerData._id, updatedData);
  };

  removeResource = () => {
    const { removeSelectedResource, workerData } = this.props;
    removeSelectedResource(workerData._id);
  };

  addResourceHours = (index) => {
    const { updateSelectedResource, workerData, sheet, user } = this.props;
    const id = uuidv4();

    let hours;
    if (index === 'new') {
      hours = workerData.hours?.length
        ? [
            ...workerData.hours,
            {
              start: new Date(sheet.hours?.start || setMinutes(new Date(), 0)),
              end: new Date(sheet.hours?.end || setMinutes(new Date(), 0)),
              id,
              isWlHours: false,
              isWlTravelTime: true,
            },
          ]
        : [
            {
              start: new Date(sheet.hours?.start || setMinutes(new Date(), 0)),
              end: new Date(sheet.hours?.end || setMinutes(new Date(), 0)),
              id,
              isWlHours: false,
              isWlTravelTime: true,
            },
          ];
    } else if (index === 'full') {
      hours = [
        ...workerData.hours,
        {
          isWlTravelTime: false,
          isWlHours: false,
          id,
        },
      ];
    } else {
      hours = workerData.hours?.length
        ? workerData.hours.map((el, i) =>
            i === index
              ? {
                  ...el,
                  isWlHours: false,
                }
              : el
          )
        : [
            {
              start: new Date(sheet.hours?.start || setMinutes(new Date(), 0)),
              end: new Date(sheet.hours?.end || setMinutes(new Date(), 0)),
              id,
              isWlHours: false,
              isWlTravelTime: true,
            },
          ];
    }

    const { hours: migratedHours, laborClassificationId: migratedLc } =
      applyWorkerLcMigration(workerData, hours);
    const updatedData = {
      ...workerData,
      hours: migratedHours,
      laborClassificationId: migratedLc,
    };

    const isValidHours = isValidTimePeriods(migratedHours);
    if (isValidHours) {
      const isInvalidHoursByTimeoffs = isHoursOverlapseTimeOffs(
        migratedHours,
        workerData.availableHours,
        sheet
      );
      if (!isInvalidHoursByTimeoffs) {
        delete updatedData.error;
      } else {
        updatedData.error = 'User has timeoff at this time';
      }
    } else {
      if (!sheet.travelTimeOnly) updatedData.error = 'Time periods cross each other';
    }
    updateSelectedResource(workerData._id, updatedData);
  };

  addTravelTime = (index) => {
    const { updateSelectedResource, workerData, user } = this.props;
    const id = uuidv4();

    let hours;
    if (index === 'new') {
      hours = workerData.hours?.length
        ? [
            ...workerData.hours,
            {
              isWlTravelTime: false,
              isWlHours: true,
              id,
            },
          ]
        : [
            {
              isWlTravelTime: false,
              isWlHours: true,
              id,
            },
          ];
    } else {
      hours = workerData.hours
        ? workerData.hours.map((el, i) => (i === index ? { ...el, isWlTravelTime: false } : el))
        : [
            {
              isWlTravelTime: false,
              isWlHours: true,
              id,
            },
          ];
    }

    const { hours: migratedHours, laborClassificationId: migratedLc } =
      applyWorkerLcMigration(workerData, hours);
    const updatedData = {
      ...workerData,
      hours: migratedHours,
      laborClassificationId: migratedLc,
    };
    updateSelectedResource(workerData._id, updatedData);
  };

  removeResourceHours = (index) => () => {
    const { updateSelectedResource, workerData, sheet } = this.props;
    // let filteredHours = workerData.hours
    //   .map((h, i) => (i === index ? { ...h, isWlHours: true } : h))
    //   .filter((el) => !(el.isWlTravelTime && el.isWlHours));
    let filteredHours = workerData.hours.filter((h, i) => i !== index);

    if (!filteredHours.length) filteredHours = null;
    const updatedData = {
      ...workerData,
      hours: filteredHours,
      startTime: null,
      startTimeDate: null,
    };

    const hoursToCheck = filteredHours || [sheet.hours];
    const isValidHours = isValidTimePeriods(hoursToCheck);
    if (isValidHours) {
      const isValidHoursByTimeoffs = !isHoursOverlapseTimeOffs(
        hoursToCheck,
        workerData.availableHours,
        sheet
      );
      if (isValidHoursByTimeoffs) {
        delete updatedData.error;
      } else {
        updatedData.error = 'User has timeoff at this time';
      }
    } else {
      if (!sheet.travelTimeOnly) updatedData.error = 'Time periods cross each other';
    }
    updateSelectedResource(workerData._id, updatedData);
  };

  removeResourceTravelTime = (index) => () => {
    const { updateSelectedResource, workerData, sheet } = this.props;

    let filteredHours;
    if (sheet.travelTimeOnly) {
      filteredHours = workerData.hours.filter((h, i) => i !== index);
    } else {
      filteredHours = workerData.hours
        .map((h, i) => (i === index ? { ...h, isWlTravelTime: true } : h))
        .filter((el) => !(el.isWlTravelTime && el.isWlHours));
    }

    if (!filteredHours.length) filteredHours = null;
    const updatedData = {
      ...workerData,
      hours: filteredHours,
    };
    updateSelectedResource(workerData._id, updatedData);
  };

  setStartTime = (value) => {
    const { updateSelectedResource, workerData, sheet } = this.props;
    const workerDayStart = workerData.startTimeDate ? differenceInDays(new Date(workerData.startTimeDate), new Date(sheet.createdAt)) : '0'
    const startTimeDate = set(
      add(new Date(sheet.createdAt), { days: +(value.dayStart ?? workerDayStart) }),
      {
        hours: value.hourStart ?? workerData.startTime?.hourStart ?? '0',
        minutes: value.minuteStart ?? workerData.startTime?.minuteStart ?? '0'
      }
    );
    const updatedData = {
      ...workerData,
      startTime: {
        ...(workerData.startTime || {}),
        ...value,
      },
      startTimeDate,
    };
    updateSelectedResource(workerData._id, updatedData);
  };

  updateStartTime = (parts) => {
    const { updateSelectedResource, workerData, sheet } = this.props;

    const updatedData = {
      ...workerData,
      startTime: {
        ...(workerData.startTime || {}),
        ...parts,
      },
    };

    const workerDayStart = workerData.startTimeDate ? differenceInDays(new Date(workerData.startTimeDate), new Date(sheet.createdAt)) : '0'
    const startTimeDate = set(
      add(new Date(sheet.createdAt), { days: +(updatedData.startTime?.dayStart ?? workerDayStart) }),
      {
        hours: updatedData.startTime?.hourStart ?? workerData.startTime?.hourStart ?? '0',
        minutes: updatedData.startTime?.minuteStart ?? workerData.startTime?.minuteStart ?? '0'
      }
    );
    updatedData.startTimeDate = startTimeDate

    updateSelectedResource(workerData._id, updatedData);
  };

  updateResourceHours = (index) => (data) => {
    const { updateSelectedResource, workerData, sheet } = this.props;
    const updatedHours = [...workerData.hours];
    updatedHours[index] = data;
    const updatedData = {
      ...workerData,
      hours: updatedHours,
    };

    const hoursToCheck = updatedHours.length ? updatedHours : [sheet.hours];
    const isValidHours = isValidTimePeriods(hoursToCheck);

    if (isValidHours) {
      const isValidHoursByTimeoffs = !isHoursOverlapseTimeOffs(
        hoursToCheck,
        workerData.availableHours,
        sheet
      );
      if (isValidHoursByTimeoffs) {
        delete updatedData.error;
      } else {
        updatedData.error = 'User has timeoff at this time';
      }
    } else {
      if (!sheet.travelTimeOnly) updatedData.error = 'Time periods cross each other';
    }
    updateSelectedResource(workerData._id, updatedData);
  };

  getResourceError = () => this.props.workerData.error;

  render() {
    const { classes, user, sheet, workerData } = this.props;
    const isAnyCropped =
      !sheet.travelTimeOnly &&
      !!workerData?.hours?.length &&
      workerData?.hours?.some((el) => el.isWlHours === true || el.isWlTravelTime === true);

    return (
      <Grid container className={classes.paddingBottom}>
        <Grid item xs={6} className={classes.paddingBottom}>
          <div className={classes.flexBox}>
            {user && (
              <div className={classes.inlineFlex}>
                <div
                  className={classnames(
                    classes.photoWrapper,
                    classes[`${user.profile.shifts?.timeOfDay?.toLowerCase()}Border`]
                  )}
                >
                  <img src={photoPlaceholder} className={classes.photo} />
                </div>
                <Typography
                  variant="body1"
                  color="textPrimary"
                  className={classnames(
                    classes.name,
                    classes[user.profile.shifts?.timeOfDay?.toLowerCase()]
                  )}
                >
                  {user.username}
                </Typography>
              </div>
            )}

            <Button
              variant="text"
              className={workerData.foreman ? classes.activeButton : classes.inactiveButton}
              onClick={this.setForeman}
              disableRipple
              disabled={!this.props.canEditCrewLeader}
            >
              (CL)
            </Button>
          </div>
        </Grid>
        <Grid item xs={6} className={classnames(classes.rtsBlock, classes.paddingBottom)}>
          <IconButton
            className={classes.closeButton}
            onClick={this.removeResource}
            aria-label="Close"
            disableRipple
            disableTouchRipple
          >
            <CloseIcon />
          </IconButton>
        </Grid>
        {(workerData.hours && !!workerData.hours.length) ? (
          <Grid item xs={12} className={classes.hoursBlock}>
            <StartTimeBlock
              sheet={sheet}
              data={workerData.startTime}
              updateStartTime={this.updateStartTime}
              setStartTime={this.setStartTime}
              canEdit={this.props.canEditStartTime}
              errorData={workerData.errorData}
            />
          </Grid>
        ) : null}

        {(workerData.hours?.length) ?
          workerData.hours.map((h, i) => (
            <Pair
              key={h.id || i}
              classes={classes}
              sheet={sheet}
              workerData={workerData}
              h={h}
              i={i}
              isAnyCropped={isAnyCropped}
              addResourceHours={this.addResourceHours}
              addTravelTime={this.addTravelTime}
              removeResourceHours={this.removeResourceHours}
              removeResourceTravelTime={this.removeResourceTravelTime}
              updateResourceHours={this.updateResourceHours}
            />
          )) : null}
        {Boolean(this.getResourceError()) && (
          <div className={classes.error}>{this.getResourceError()}</div>
        )}
        <WorkerLcBlock
          classes={classes}
          sheet={sheet}
          workerData={workerData}
          isPicking={this.state.isPickingWorkerLc}
          setIsPicking={this.setIsPickingWorkerLc}
          setWorkerLc={this.setWorkerLc}
        />
        {
          <Grid
            container
            item
            direction="row"
            xs={12}
            alignItems={'center'}
            justifyContent={'space-around'}
            className={classes.paddingBottom}
          >
            {!sheet.travelTimeOnly &&
              !workerData?.hours?.[workerData?.hours?.length - 1]?.isWlHours && (
                // <Grid item xs={4} md={2} className={classes.textCenter}>
                  <Button
                    variant="text"
                    onClick={() => {
                      if (!workerData?.hours?.length) return this.addResourceHours('new');
                      this.addResourceHours(isAnyCropped ? 'full' : 'new');
                    }}
                    className={classes.addButton}
                    disableRipple
                    disableTouchRipple
                  >
                    + Add Hours
                  </Button>
                // </Grid>
              )}
            {(!workerData?.hours?.length || sheet.travelTimeOnly) && (
              // <Grid item xs={6} md={4} className={classes.textCenter}>
                <Button
                  variant="text"
                  onClick={() => this.addTravelTime('new')}
                  className={classnames(classes.addButton, classes.textCenter)}
                  disableRipple
                  disableTouchRipple
                >
                  + Individual Travel Time
                </Button>
              // </Grid>
            )}
            <WorkerLcAddButton
              classes={classes}
              sheet={sheet}
              workerData={workerData}
              isPicking={this.state.isPickingWorkerLc}
              setIsPicking={this.setIsPickingWorkerLc}
            />
          </Grid>
        }
        <Grid container>
          <Grid item xs={12} style={{ margin: '15px 0' }}>
            <Divider />
          </Grid>
        </Grid>
      </Grid>
    );
  }
}

SelectedUser.propTypes = {
  classes: propTypes.object.isRequired,
  user: propTypes.object.isRequired,
  sheet: propTypes.object.isRequired,
  workerData: propTypes.object.isRequired,
  updateSelectedResource: propTypes.func.isRequired,
  removeSelectedResource: propTypes.func.isRequired,
  canEditStartTime: propTypes.bool,
  canEditCrewLeader: propTypes.bool,
};

export default withStyles(styles)(SelectedUser);
